import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function CreateProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [store, setStore] = useState(null);
  const [designFile, setDesignFile] = useState(null);

  // Update your initial formData state
const [formData, setFormData] = useState({
  name: '',
  description: '',
  category: '',
  tags: '',
  designImage: '',
  allowCustomization: true,
  lowStockThreshold: 10,
  variants: [{
    name: 'Default',
    basePrice: 0,
    markup: 0,
    finalPrice: 0,
    stock: 100,
    color: '#000000',
    size: 'M',
    material: 'cotton'
  }],
  printSettings: {
    placement: 'front',
    size: 'medium',
    colors: ['#000000']
  }
});

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const { data } = await api.get(`/stores/${slug}`);
        setStore(data.data.store);
      } catch (err) {
        setError('Failed to load store information');
        console.error(err);
      }
    };
    
    fetchStore();
  }, [slug]);

  useEffect(() => {
  const fetchStoreAndUser = async () => {
    try {
      const [storeRes, userRes] = await Promise.all([
        api.get(`/stores/${slug}`),
        api.get(`/auth/me`)
      ]);

      const fetchedStore = storeRes.data.data.store;
      const currentUser = userRes.data.data.user;

      // Check ownership
      if (
        currentUser.role !== 'store_owner' ||
        currentUser.storeId._id !== fetchedStore._id
      ) {
        setError('You are not the store owner of this store.');
        return;
      }

      setStore(fetchedStore);
    } catch (err) {
      console.error(err);
      setError('Failed to load store or user information');
    }
  };

  fetchStoreAndUser();
}, [slug]);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleVariantChange = (index, e) => {
    const newVariants = [...formData.variants];
    newVariants[index][e.target.name] = e.target.value;
    
    if (e.target.name === 'price') {
      newVariants[index].finalPrice = parseFloat(e.target.value);
    }
    
    setFormData({
      ...formData,
      variants: newVariants
    });
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { 
          name: `Variant ${formData.variants.length + 1}`, 
          price: 0, 
          finalPrice: 0,
          stock: 100,
          color: '#000000',
          size: 'M',
          material: 'cotton'
        }
      ]
    });
  };

  const removeVariant = (index) => {
    const newVariants = [...formData.variants];
    newVariants.splice(index, 1);
    setFormData({
      ...formData,
      variants: newVariants
    });
  };

  const handleFileChange = (e) => {
  if (e.target.files && e.target.files[0]) {
    setDesignFile(e.target.files[0]);
    // Clear the URL field if file is selected
    setFormData({...formData, designImage: ''});
  }
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const formDataToSend = new FormData();
    
    // Append basic product info
    formDataToSend.append('name', formData.name);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('tags', formData.tags || '');
    formDataToSend.append('allowCustomization', formData.allowCustomization || true);
    formDataToSend.append('lowStockThreshold', formData.lowStockThreshold || 10);
    
    // Process variants
    const processedVariants = formData.variants.map(variant => ({
      name: variant.name,
      material: variant.material,
      colors: [{ 
        name: variant.name || 'Custom', 
        hex: variant.color || '#000000' 
      }],
      sizes: [{ 
        name: variant.size || 'One Size' 
      }],
      basePrice: parseFloat(variant.basePrice) || 0,
      markup: parseFloat(variant.markup) || 0,
      finalPrice: parseFloat(variant.price) || 0,
      stock: parseInt(variant.stock) || 0
    }));
    
    formDataToSend.append('variants', JSON.stringify(processedVariants));
    formDataToSend.append('printSettings', JSON.stringify(formData.printSettings));
    
    // Handle design upload
    if (designFile) {
      formDataToSend.append('design', designFile);
    } else if (formData.designImage) {
      formDataToSend.append('designImage', formData.designImage);
    }

    const { data } = await api.post('/products', formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (data.success) {
      navigate(`/store/${slug}/products/${data.data.product._id}`);
    }
  } catch (err) {
    setError(err.response?.data?.message || 'Failed to create product');
    console.error('Error creating product:', err);
  } finally {
    setLoading(false);
  }
};


  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (error) {
  return (
    <div className="min-h-screen bg-gray-50 flex justify-center items-center">
      <div className="text-center text-red-600 font-semibold text-xl">
        {error}
      </div>
    </div>
  );
}


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Add New Product to {store.name}</h1>
          <p className="mt-2 text-sm text-gray-600">
            Fill out the form below to add a new product to your store
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Product Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                name="category"
                id="category"
                value={formData.category}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                required
              >
                <option value="">Select a category</option>
                <option value="tshirt">T-Shirts</option>
                <option value="hoodie">Hoodies</option>
                <option value="tank-top">Tank Top</option>
                <option value="other">Others</option>
              </select>
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                Tags (comma separated)
              </label>
              <input
                type="text"
                name="tags"
                id="tags"
                value={formData.tags}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                placeholder="e.g. gaming, anime, custom"
              />
            </div>

            <div>
                <label htmlFor="designFile" className="block text-sm font-medium text-gray-700">
                    Or Upload Design File
                </label>
                <input
                    type="file"
                    id="designFile"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="mt-1 block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-pink-50 file:text-pink-700
                    hover:file:bg-pink-100"
                />
                {designFile && (
                    <p className="mt-1 text-sm text-gray-500">
                    Selected file: {designFile.name}
                    </p>
                )}

                <input
                type="text"
                name="designImage"
                id="designImage"
                value={formData.designImage || ''}
                onChange={(e) =>
                    setFormData({
                    ...formData,
                    designImage: e.target.value
                    })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                placeholder="https://example.com/image.png"
                required={!designFile}  // Only required if no file is selected
                disabled={!!designFile} // Disable if file is selected
                />
            </div>

            <div>
              <label htmlFor="designImage" className="block text-sm font-medium text-gray-700">
                Design Image URL
              </label>
              <input
                type="text"
                name="designImage"
                id="designImage"
                value={formData.designImage || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    designImage: e.target.value
                  })
                }
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                placeholder="https://example.com/image.png"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Variants</h3>
                <button
                  type="button"
                  onClick={addVariant}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                >
                  Add Variant
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {formData.variants.map((variant, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor={`variant-name-${index}`} className="block text-sm font-medium text-gray-700">
                          Variant Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id={`variant-name-${index}`}
                          value={variant.name}
                          onChange={(e) => handleVariantChange(index, e)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor={`variant-price-${index}`} className="block text-sm font-medium text-gray-700">
                          Price (₹)
                        </label>
                        <input
                          type="number"
                          name="price"
                          id={`variant-price-${index}`}
                          value={variant.price}
                          onChange={(e) => handleVariantChange(index, e)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor={`variant-stock-${index}`} className="block text-sm font-medium text-gray-700">
                          Stock
                        </label>
                        <input
                          type="number"
                          name="stock"
                          id={`variant-stock-${index}`}
                          value={variant.stock}
                          onChange={(e) => handleVariantChange(index, e)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                          min="0"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor={`variant-color-${index}`} className="block text-sm font-medium text-gray-700">
                          Color
                        </label>
                        <input
                          type="color"
                          name="color"
                          id={`variant-color-${index}`}
                          value={variant.color}
                          onChange={(e) => handleVariantChange(index, e)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-1 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm h-10"
                        />
                      </div>

                      <div>
                        <label htmlFor={`variant-material-${index}`} className="block text-sm font-medium text-gray-700">
                          Material
                        </label>
                        <select
                          name="material"
                          id={`variant-material-${index}`}
                          value={variant.material}
                          onChange={(e) => handleVariantChange(index, e)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                          required
                        >
                          <option value="cotton">Cotton</option>
                          <option value="polyester">Polyester</option>
                          <option value="blend">Blend</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor={`variant-size-${index}`} className="block text-sm font-medium text-gray-700">
                            Size
                        </label>
                        <select
                            name="size"
                            id={`variant-size-${index}`}
                            value={variant.size}
                            onChange={(e) => handleVariantChange(index, e)}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm"
                            required
                        >
                            <option value="XS">XS</option>
                            <option value="S">S</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="XL">XL</option>
                            <option value="XXL">XXL</option>
                            <option value="One Size">One Size</option>
                        </select>
                        </div>
                        </div>

                    {formData.variants.length > 1 && (
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Remove Variant
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate(`/store/${slug}/products`)}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}